import { action, computed, observable } from 'mobx';
import { v4 } from 'uuid';

import { TranslatedMessage } from '../models/TranslatedMessage';
import { IconName } from '../components/Icon/Icon';
import { getErrorMessage } from '../services/Utils';

export enum NotificationType {
  INFO,
  WARNING,
  ERROR
}

export enum NotificationDuration {
  SHORT = 3000,
  MEDIUM = 4500,
  LONG = 6000,
  PERSISTENT = Infinity
}

type MessageConfiguration = {
  type: NotificationType;
  duration: NotificationDuration;
  icon?: IconName | 'spinner';
  content: JSX.Element | TranslatedMessage | string | string[];
};

export type NotificationIcon = MessageConfiguration['icon'];

type NotificationDismissAction = () => void;

type Notification = MessageConfiguration & {
  id: string;
};

export class NotificationsStore {
  @observable
  private notificationStack: Notification[] = [];

  @observable
  private timeout?: ReturnType<typeof setTimeout> | null;

  info(content: MessageConfiguration['content'], icon?: NotificationIcon, duration?: NotificationDuration) {
    return this.queueNotification({
      type: NotificationType.INFO,
      icon,
      duration: duration ? duration : NotificationDuration.MEDIUM,
      content: content
    });
  }

  warning(content: MessageConfiguration['content'], icon?: NotificationIcon, duration?: NotificationDuration) {
    return this.queueNotification({
      type: NotificationType.WARNING,
      icon,
      duration: duration ? duration : NotificationDuration.LONG,
      content: content
    });
  }

  error(content: MessageConfiguration['content'], icon?: NotificationIcon, duration?: NotificationDuration) {
    return this.queueNotification({
      type: NotificationType.ERROR,
      icon,
      duration: duration ? duration : NotificationDuration.SHORT,
      content: content
    });
  }

  persistent(content: MessageConfiguration['content'], icon?: NotificationIcon) {
    return this.queueNotification({
      type: NotificationType.WARNING,
      icon,
      duration: NotificationDuration.PERSISTENT,
      content: content
    });
  }

  private queueNotification(config: MessageConfiguration): NotificationDismissAction {
    const id = v4();
    this.notificationStack.push({
      id,
      ...config
    });

    if (this.timeout === undefined) {
      this.displayNotification();
    }

    return () => this.cancelNotification(id);
  }

  private cancelNotification(id: string) {
    if (this.currentNotification?.id === id) {
      this.dismissNotification();
      if (this.notificationStack.length > 0) {
        this.displayNotification();
      }
    } else {
      const notificationIndex = this.notificationStack.findIndex(notification => notification.id === id);
      if (notificationIndex !== -1) {
        this.notificationStack.splice(notificationIndex, 1);
      }
    }
  }

  @computed
  get currentNotification() {
    return this.notificationStack.length > 0 && this.timeout !== undefined ? this.notificationStack[0] : undefined;
  }

  @action.bound
  private displayNotification() {
    const notificationConfig = this.notificationStack[0];

    if (!notificationConfig) {
      return;
    }

    if (notificationConfig.duration === Infinity) {
      this.timeout = null;
    } else {
      this.timeout = setTimeout(() => {
        this.dismissNotification();
        if (this.notificationStack.length > 0) {
          this.displayNotification();
        }
      }, notificationConfig.duration);
    }
  }

  @action.bound
  private dismissNotification() {
    this.timeout = undefined;
    this.notificationStack.shift();
  }

  async fromPromise(
    promise: Promise<void>,
    pendingMessage: TranslatedMessage,
    doneMessage?: TranslatedMessage,
    errorMessage?: TranslatedMessage
  ) {
    const dismissNotification = this.persistent(pendingMessage, 'spinner');
    try {
      await promise;
      dismissNotification();
      if (doneMessage) {
        this.info(doneMessage, 'check');
      }
      return 'ok';
    } catch (e) {
      dismissNotification();
      this.error(errorMessage ?? getErrorMessage(e));
    }
  }
}
